import { useEffect, useState } from 'react';

/**
 * Breakpoints, in one place.
 *
 * These are the same numbers `config/theme/responsive.css` keys off. A layout
 * decision that has to be made in JS (does this pane own the whole screen?
 * should this control render at all?) has to agree with the stylesheet, and the
 * only way to guarantee that is to spell the boundary once.
 *
 * PHONE   ≤ 768px — one column: the list OR the pane, never both.
 * TABLET  ≤ 1024px — rail + list + pane, tightened; no details column.
 * Above that is the full desktop shell.
 */
export const BREAKPOINTS = Object.freeze({
  PHONE: 768,
  TABLET: 1024,
});

export const MEDIA = Object.freeze({
  PHONE: `(max-width: ${BREAKPOINTS.PHONE}px)`,
  TABLET_DOWN: `(max-width: ${BREAKPOINTS.TABLET}px)`,
  /** No hover-capable pointer — a phone or a tablet, not a mouse. */
  TOUCH: '(hover: none)',
});

/**
 * Subscribes to a media query.
 *
 * The initial value is read during the first render rather than in an effect,
 * so the tree never paints one frame of the desktop layout on a phone.
 * `addEventListener` on a MediaQueryList is not available on older Safari,
 * hence the `addListener` fallback — without it the hook silently stops
 * updating on rotation there.
 */
export const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const list = window.matchMedia(query);
    const sync = (event) => setMatches(event.matches);

    // The query may have changed between render and effect (rotation during
    // mount), so re-read before subscribing.
    setMatches(list.matches);

    if (list.addEventListener) {
      list.addEventListener('change', sync);
      return () => list.removeEventListener('change', sync);
    }

    list.addListener(sync);
    return () => list.removeListener(sync);
  }, [query]);

  return matches;
};

/** True on a phone-width viewport, where a pane covers the list. */
export const useIsPhone = () => useMediaQuery(MEDIA.PHONE);

/** True when the primary pointer cannot hover — used to un-hide hover-only controls. */
export const useIsTouch = () => useMediaQuery(MEDIA.TOUCH);

export default useMediaQuery;
